import { memo, useEffect, useMemo, useState } from 'react';

import { State } from '@progress/kendo-data-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { ExamGridModel, FileModel, TagModel, WorklistViewModel } from 'models';

import { DataResult } from 'core/api';
import { useBoolean, useEvent, useSignalRQueryInvalidation } from 'core/hooks';
import { ColumnState, Page } from 'core/ui';

import { useQueryPatient } from 'features/api';
import { useParsedShareAccessToken } from 'features/auth';
import { useSignalRRefresh } from 'features/auth/hooks/useSignalRRefresh';
import { ExamGridDataStateChangeEvent } from 'features/exam';
import { useExamsGridQuery } from 'features/exam/hooks';
import { UploadWindow, ViewFileWindow } from 'features/file/fragments';
import { FileViewerProvider } from 'features/patient/contexts/FileViewerContext';
import { FileViewerIntegrated } from 'features/patient/fragments/FileViewerIntegrated';
import { PatientEditExamGrid } from 'features/patient/fragments/PatientEditExamGrid';
import { PatientEditHeader } from 'features/patient/fragments/PatientEditHeader';
import { PatientFiles } from 'features/patient/fragments/PatientFiles';
import { PatientGridService } from 'features/patient/services/patient-grid-service';
import { SettingsHelpers, useUserSettings } from 'features/settings';
import { WellKnownSettingName } from 'features/settings/types/WellKnownSettingName';
import { useShareSenderInfo } from 'features/share/hooks/useShareSenderInfo';

import { ShareTabNavigation } from './ShareTabNavigation';
import { ShareTopHeader } from './ShareTopHeader';

const EMPTY_DATA_RESULT: DataResult<ExamGridModel> = { data: [], total: 0 };

export const ShareHome = memo(() => {
  const shareToken = useParsedShareAccessToken(true);
  const { sharePatientId, shareLinkId, message } = shareToken;
  const navigate = useNavigate();
  const location = useLocation();
  const { '*': pathSuffix = '' } = useParams();

  // Parse tag IDs from share token message for upload initialization
  const initialTagIdsFromShare = useMemo(() => {
    if (!message) return null;

    console.log('[ShareHome] Parsing share token message:', message);

    // Parse TAG_ID:123 format
    const singleTagMatch = /^TAG_ID:(\d+)$/.exec(message);
    if (singleTagMatch) {
      const tagId = parseInt(singleTagMatch[1], 10);
      console.log('[ShareHome] Extracted single tag ID:', tagId);
      return [tagId];
    }

    // Parse TAG_IDS:123,456,789 format
    const multipleTagsMatch = /^TAG_IDS:([\d,]+)$/.exec(message);
    if (multipleTagsMatch) {
      const tagIds = multipleTagsMatch[1]
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));
      console.log('[ShareHome] Extracted multiple tag IDs:', tagIds);
      return tagIds.length > 0 ? tagIds : null;
    }

    // Parse CASE:CaseName format (fallback)
    const caseMatch = /^CASE:(.+)$/.exec(message);
    if (caseMatch) {
      console.log('[ShareHome] Case-based upload detected:', caseMatch[1]);
      // For case-based uploads, we'll return null and let PatientFiles handle the context
      return null;
    }

    console.log('[ShareHome] No tag information found in message:', message);
    return null;
  }, [message]);

  // Get user settings to check if Files tab should be shown
  const { userSettings } = useUserSettings(false);

  // Fetch patient data to get location_Id for worklist queries
  const [{ data: patient }] = useQueryPatient(sharePatientId);

  // Get sender information for the share (when API supports it)
  const { senderName, senderEmail, description } = useShareSenderInfo();

  // Determine current tab from URL
  const isInsightRoute = location.pathname.includes('/insight') || pathSuffix.includes('insight');
  const isFilesRoute =
    location.pathname.includes('/files') || pathSuffix.includes('files') || location.pathname.includes('/upload') || pathSuffix.includes('upload');
  const isUploadRoute = location.pathname.includes('/upload') || pathSuffix.includes('upload');

  // When in upload mode, we don't show tabs at all - just the upload interface
  const isInUploadMode = isUploadRoute;

  // Determine if Files tab should be shown - always enable for upload routes
  /*const showFilesTab = useMemo(() => {
    const settingValue = SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTabShare, false)?.value === 'true';
    // Always show Files tab when on upload routes, regardless of setting
    return settingValue || isUploadRoute;
  }, [userSettings, isUploadRoute]);*/
  const showFilesTab = true;

  // Always show Exams tab unless we're in upload mode
  const showExamsTab = !isInUploadMode;

  const isViewRoute = location.pathname.includes('/view') || pathSuffix.includes('view');
  const isExamsRoute = location.pathname.includes('/exams') || pathSuffix.includes('exams');
  const isFileViewRoute = /\/view\/.*\d+$/.exec(location.pathname) !== null;

  // Insight tab includes insight, files, and view routes (files route works same as insight for compatibility)
  const isInsightTabActive = isInsightRoute || isFilesRoute || isViewRoute;
  const isExamsTabActive = isExamsRoute;

  const [selectedTabView, setSelectedTabView] = useState<'Exams' | 'Files'>(
    // When in upload mode, default to Files tab, otherwise use normal logic
    isInUploadMode ? 'Files' : isExamsTabActive && showExamsTab ? 'Exams' : (isInsightTabActive || isUploadRoute) && showFilesTab ? 'Files' : 'Exams',
  );

  const [dataState, setDataState] = useState<State>(PatientGridService.getDefaultPatientExamDataState(sharePatientId));
  const [columnsState, setColumnsState] = useState<Record<string, ColumnState>>({});
  const [isViewAddAttachmentsModalVisible, { setTrue: openViewAddAttachmentsModal, setFalse: closeViewAddAttachmentsModal }] = useBoolean(false);
  const [isViewFinalReportModalVisible, { setTrue: openViewFinalReportModal, setFalse: closeViewFinalReportModal }] = useBoolean(false);
  const [selectedExamId, setSelectedExamId] = useState<number | null>(null);
  const [viewFile, setViewFile] = useState<FileModel | null>(null);

  // File viewer state for integrated viewing
  const [integratedViewerFile, setIntegratedViewerFile] = useState<FileModel | null>(null);
  const [integratedViewerContext, setIntegratedViewerContext] = useState<FileModel[]>([]);

  // Use optimized exam grid query with column state optimization
  const examsQuery = useExamsGridQuery({
    dataState,
    authMode: 'share-required',
    columnsState,
    enabled: true,
    includeFiles: true, // Include files to ensure finalReportFile is available
  });

  // Process exam data to set finalReportFile property (similar to PatientEdit.tsx)
  const examsGridData = useMemo(() => {
    if (!examsQuery.data?.data) return examsQuery.data;

    const processedExams = PatientGridService.initializePatientGrid(
      examsQuery.data.data,
      false, // isOpo - assuming false for share context
      patient,
    );

    return {
      ...examsQuery.data,
      data: processedExams,
    };
  }, [examsQuery.data, patient]);

  // Create a callback to receive patient file data refresh function from PatientFiles
  const [patientFileDataRefresh, setPatientFileDataRefresh] = useState<(() => Promise<void>) | null>(null);

  // Store file operation handlers from PatientFiles
  const [fileOperationHandlers, setFileOperationHandlers] = useState<{
    handleFileDeleted: (fileId: string | number) => Promise<void>;
    handleFileRenamed: (file: FileModel, newName: string) => Promise<void>;
    handleFileDescriptionUpdated: (file: FileModel, description: string) => Promise<void>;
    handleFileTimestampsUpdated: (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => Promise<void>;
    handleFileTagsUpdated: (fileId: number, tagIds: number[]) => Promise<void>;
    handleFileDownloaded: (file: FileModel) => Promise<void>;
  } | null>(null);

  // Store tag data from PatientFiles
  const [tagData, setTagData] = useState<{
    allFlatTags: TagModel[];
    allHierarchicalTags: TagModel[];
    tagIdToTagMap: Map<number, TagModel>;
    isLoading: boolean;
  }>({
    allFlatTags: [],
    allHierarchicalTags: [],
    tagIdToTagMap: new Map(),
    isLoading: true,
  });

  // Store worklist data from PatientFiles
  const [worklistData, setWorklistData] = useState<{
    selectedWorklist: WorklistViewModel | null;
    allWorklists: WorklistViewModel[];
    currentPath: string;
  }>({
    selectedWorklist: null,
    allWorklists: [],
    currentPath: '',
  });

  // Form submissions data from PatientFiles
  const [formData, setFormData] = useState<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formSubmissions: any[];
    areFormSubmissionsLoading: boolean;
    handleFormSubmissionsUpdated: () => Promise<void>;
  } | null>(null);

  // State for filter sidebar
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  // Upload-only mode: prevents files view from rendering for users without files access after upload
  const [uploadOnlyMode, setUploadOnlyMode] = useState(false);

  // Sync tab state with URL and handle worklist persistence
  useEffect(() => {
    // Skip navigation logic in upload-only mode
    if (uploadOnlyMode) {
      return;
    }

    const newTab = isExamsTabActive ? 'Exams' : (isInsightTabActive || isUploadRoute) && showFilesTab ? 'Files' : 'Exams';
    if (newTab !== selectedTabView) {
      setSelectedTabView(newTab);
    }

    // Handle file viewer integration
    if (isFileViewRoute && !integratedViewerFile) {
      // Extract file ID from URL and set up integrated viewer
      const fileIdMatch = /\/(\d+)$/.exec(location.pathname);
      if (fileIdMatch) {
        const fileId = parseInt(fileIdMatch[1], 10);
        // File will be loaded by PatientFiles component
      }
    } else if (!isFileViewRoute && integratedViewerFile) {
      // Clear integrated viewer when not on file view route
      setIntegratedViewerFile(null);
    }
  }, [
    isExamsTabActive,
    isInsightTabActive,
    isUploadRoute,
    isFileViewRoute,
    selectedTabView,
    integratedViewerFile,
    location.pathname,
    showFilesTab,
    uploadOnlyMode,
  ]);

  // Auto-redirect to default view when accessing base share URL
  useEffect(() => {
    // Skip auto-redirect in upload-only mode
    if (uploadOnlyMode) {
      return;
    }

    // Check if we're on the base share route (no sub-path)
    const isBaseShareRoute = location.pathname === `/share/${shareLinkId}` || location.pathname === `/share/${shareLinkId}/`;

    if (isBaseShareRoute && shareLinkId) {
      // Redirect to files view if enabled, otherwise to exams
      if (showFilesTab) {
        navigate(`/share/${shareLinkId}/files`, { replace: true });
      } else {
        navigate(`/share/${shareLinkId}/exams`, { replace: true });
      }
    }
  }, [location.pathname, shareLinkId, navigate, showFilesTab, uploadOnlyMode]);

  // Store worklist selection to persist across tab changes
  const [persistedWorklistSelection, setPersistedWorklistSelection] = useState<{
    selectedWorklist: WorklistViewModel | null;
    allWorklists: WorklistViewModel[];
    currentPath: string;
  } | null>(null);

  const handleDataStateChange = useEvent((event: ExamGridDataStateChangeEvent) => {
    setDataState(event.dataState);
  });

  const handleViewExam = useEvent((exam: ExamGridModel) => {
    navigate(`/share/${shareLinkId}/file-viewer/${exam.id}?feature=exams`);
  });

  const handleViewAddAttachmentsClick = useEvent((exam: ExamGridModel) => {
    openViewAddAttachmentsModal();
    setSelectedExamId(exam.id);
  });

  const handleViewAddAttachmentsChange = useEvent(() => {
    examsQuery.refetch();
  });

  const handleViewAddAttachmentsClose = useEvent(() => {
    closeViewAddAttachmentsModal();
    setSelectedExamId(null);
  });

  const handleViewFinalReport = useEvent((exam: ExamGridModel) => {
    if (exam.finalReportFile == null) {
      console.warn('Final report file is not available for exam:', exam.id);
      return; // Gracefully handle missing final report instead of throwing error
    }
    setViewFile(exam.finalReportFile ?? null);
    openViewFinalReportModal();
  });

  const handleViewFinalReportClose = useEvent(() => {
    setViewFile(null);
    closeViewFinalReportModal();
  });

  // Handle tab navigation with proper URL sync
  const handleTabChange = useEvent((tab: 'Exams' | 'Files') => {
    // Prevent navigation to Files tab if it's not enabled
    if (tab === 'Files' && !showFilesTab) {
      return;
    }

    setSelectedTabView(tab);
    switch (tab) {
      case 'Exams':
        navigate(`/share/${shareLinkId}/exams`, { replace: true });
        break;
      case 'Files':
        // For compatibility, check current URL and navigate to appropriate path
        if (location.pathname.includes('/upload')) {
          // If currently on /upload route, stay on /upload for upload mode
          navigate(`/share/${shareLinkId}/upload`, { replace: true });
        } else if (location.pathname.includes('/files')) {
          // If currently on /files route, stay on /files for compatibility
          navigate(`/share/${shareLinkId}/files`, { replace: true });
        } else {
          // Otherwise, navigate to /insight (default behavior)
          navigate(`/share/${shareLinkId}/insight`, { replace: true });
        }
        break;
    }
  });

  // Handle file viewing in share context
  const handleViewFile = useEvent((file: FileModel, context: FileModel[]) => {
    setIntegratedViewerFile(file);
    setIntegratedViewerContext(context);

    // Navigate to view route within share context - preserve worklist context if available
    const currentPath = location.pathname;
    const baseSharePath = `/share/${shareLinkId}/view`;

    // Check if we're currently in a worklist context
    const worklistRegex = new RegExp(`/share/${shareLinkId}/view/([^/]+)/\\d+`);
    const worklistMatch = worklistRegex.exec(currentPath);
    if (worklistMatch && worklistMatch[1] !== file.id.toString()) {
      // We're in a worklist context, preserve it
      const worklistSlug = worklistMatch[1];
      navigate(`${baseSharePath}/${worklistSlug}/${file.id}`, { replace: true });
    } else {
      // Default view
      navigate(`${baseSharePath}/${file.id}`, { replace: true });
    }
  });

  // Handle closing integrated file viewer
  const handleCloseViewerIntegrated = useEvent(() => {
    setIntegratedViewerFile(null);
    setIntegratedViewerContext([]);

    // Navigate back to appropriate tab - preserve worklist context if available
    const currentPath = location.pathname;

    // Determine if we should return to /files or /insight based on current context
    const shouldReturnToFiles = currentPath.includes('/files') || currentPath.includes('/view');
    const baseSharePath = shouldReturnToFiles ? `/share/${shareLinkId}/files` : `/share/${shareLinkId}/insight`;

    // Check if we're currently in a worklist context
    const worklistRegex = new RegExp(`/share/${shareLinkId}/view/([^/]+)/\\d+`);
    const worklistMatch = worklistRegex.exec(currentPath);
    if (worklistMatch) {
      // Return to worklist view
      const worklistSlug = worklistMatch[1];
      navigate(`${baseSharePath}/${worklistSlug}`, { replace: true });
    } else {
      // Return to default view
      navigate(baseSharePath, { replace: true });
    }
  });

  // Handle file selection in integrated viewer (for next/back navigation)
  const handleFileSelectionInViewer = useEvent((file: FileModel) => {
    const currentFileId = integratedViewerFile?.id;
    if (file.id !== currentFileId) {
      setIntegratedViewerFile(file);
      // Update URL to reflect the new file being viewed - use view approach
      const currentPath = location.pathname;
      const baseSharePath = `/share/${shareLinkId}/view`;

      // Check if we're in a worklist context
      const worklistRegex = new RegExp(`${baseSharePath}/([^/]+)/\\d+`);
      const worklistMatch = worklistRegex.exec(currentPath);
      if (worklistMatch) {
        // Preserve worklist context
        const worklistSlug = worklistMatch[1];
        navigate(`${baseSharePath}/${worklistSlug}/${file.id}`, { replace: true });
      } else {
        // Default view
        navigate(`${baseSharePath}/${file.id}`, { replace: true });
      }
    }
  });

  // Enhanced file operation handlers that use PatientFiles centralized handlers and close viewer after deletion
  const handleFileDeleted = useEvent(async (fileId: string | number) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    try {
      await fileOperationHandlers.handleFileDeleted(fileId);
      // Close viewer after deletion
      handleCloseViewerIntegrated();
    } catch (error) {
      // Error handling is done in PatientFiles
      console.error('Delete file error in ShareHome:', error);
    }
  });

  const handleFileRenamed = useEvent(async (file: FileModel, newName: string) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    return fileOperationHandlers.handleFileRenamed(file, newName);
  });

  const handleFileDescriptionUpdated = useEvent(async (file: FileModel, description: string) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    return fileOperationHandlers.handleFileDescriptionUpdated(file, description);
  });

  const handleFileTimestampsUpdated = useEvent(async (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    return fileOperationHandlers.handleFileTimestampsUpdated(file, captureTime, eventTime);
  });

  const handleFileTagsUpdated = useEvent(async (fileId: number, tagIds: number[]) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    return fileOperationHandlers.handleFileTagsUpdated(fileId, tagIds);
  });

  const handleFileDownloaded = useEvent(async (file: FileModel) => {
    if (!fileOperationHandlers) {
      console.error('File operation handlers not available');
      return;
    }
    return fileOperationHandlers.handleFileDownloaded(file);
  });

  // Handle upload mode changes with permission check
  const handleUploadModeChange = useEvent((isUploadMode: boolean) => {
    console.log('Upload mode changed:', isUploadMode);

    // When exiting upload mode, check if this is an upload route (QR upload scenario)
    if (!isUploadMode && isUploadRoute) {
      // Check if user has ShowFilesTabShare setting (without upload route override)
      const hasFilesTabSetting = SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTabShare, false)?.value === 'true';

      if (!hasFilesTabSetting) {
        // User doesn't have Files tab access - enter upload-only mode
        console.log('Upload completed on upload route, user lacks files access, entering upload-only mode');

        // Set upload-only mode to prevent files view from rendering
        setUploadOnlyMode(true);

        // Important: Return early to prevent any further navigation logic
        return;
      } else {
        // User has Files tab access - show success and allow normal navigation
        console.log('Upload completed on upload route, user has files access, allowing normal navigation');

        // Important: Return early to prevent any further navigation logic
        return;
      }
    }

    // IMPROVED: When exiting upload mode, check permissions more thoroughly
    if (!isUploadMode) {
      // Check if user has ShowFilesTabShare setting (without upload route override)
      const hasFilesTabSetting = SettingsHelpers.findSettingByName(userSettings ?? [], WellKnownSettingName.ShowFilesTabShare, false)?.value === 'true';

      // ADDED: Additional check - if we're currently on any files-related route and user doesn't have permission
      const isOnFilesRoute = location.pathname.includes('/files') || location.pathname.includes('/insight') || location.pathname.includes('/view');
      const shouldRedirectToExams = !hasFilesTabSetting && isOnFilesRoute && showExamsTab;

      if (shouldRedirectToExams) {
        console.log('User exited upload mode without ShowFilesTabShare setting, redirecting from files-related route to exams');
        navigate(`/share/${shareLinkId}/exams`, { replace: true });
        // ADDED: Also update the tab state to reflect the navigation
        setSelectedTabView('Exams');
        return;
      }

      // ADDED: Handle case where user is on Files tab but shouldn't have access
      if (!hasFilesTabSetting && selectedTabView === 'Files' && showExamsTab) {
        console.log('User exited upload mode without ShowFilesTabShare setting, redirecting to exams tab');
        navigate(`/share/${shareLinkId}/exams`, { replace: true });
        setSelectedTabView('Exams');
        return;
      }

      // ADDED: Handle edge case where user doesn't have Files tab permission and no Exams tab
      if (!hasFilesTabSetting && !showExamsTab) {
        console.warn('User has no tab permissions after upload exit, closing window/page');

        // Try to close the window first
        try {
          window.close();
        } catch (error) {
          console.warn('Could not close window:', error);
        }

        // If window.close() doesn't work (blocked by browser), try other approaches
        setTimeout(() => {
          // If we're still here after trying to close, the window didn't close
          console.log('Window did not close automatically, trying alternative approaches');

          // Try to go back in browser history
          if (window.history.length > 1) {
            window.history.back();
          } else {
            // As a last resort, navigate to a blank page or show a "please close" message
            document.body.innerHTML =
              '<div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:Arial,sans-serif;text-align:center;"><div><h2>Upload Complete</h2><p>You can now close this window.</p></div></div>';
          }
        }, 100);

        return;
      }
    }
  });

  // Callback to receive the refresh function from PatientFiles
  const handlePatientFilesReady = useEvent((refreshFunction: () => Promise<void>) => {
    setPatientFileDataRefresh(() => refreshFunction);
  });

  // Callback to receive file operation handlers from PatientFiles
  const handleFileOperationsReady = useEvent((handlers: typeof fileOperationHandlers) => {
    setFileOperationHandlers(handlers);
  });

  // Callback to receive tag data from PatientFiles
  const handleTagDataReady = useEvent((receivedTagData: typeof tagData) => {
    setTagData(receivedTagData);
  });

  // Callback to receive worklist data
  const handleWorklistDataReady = useEvent(
    (receivedWorklistData: { selectedWorklist: WorklistViewModel | null; allWorklists: WorklistViewModel[]; currentPath: string }) => {
      setWorklistData(receivedWorklistData);
      setPersistedWorklistSelection(receivedWorklistData);
    },
  );

  // Callback to receive form submissions data from PatientFiles
  const handleFormSubmissionsReady = useEvent((receivedFormData: typeof formData) => {
    setFormData(receivedFormData);
  });

  // Use SignalR for real-time updates
  useSignalRRefresh({
    patientId: sharePatientId,
    onPatientFileUploaded: useEvent(async () => {
      await patientFileDataRefresh?.();
    }),
    onFileDataChanged: useEvent(async () => {
      await patientFileDataRefresh?.();
    }),
  });

  // Add SignalR integration for automatic share exam refresh
  useSignalRQueryInvalidation({
    patientId: sharePatientId,
    enableSmartInvalidation: true,
    enableDebugLogging: true,
    debounceMs: 500,

    // Invalidate share exam queries on relevant events
    invalidateOnExamChanged: ['exams-grid'],
    invalidateOnPatientChanged: ['exams-grid'],
    invalidateOnFileChanged: ['exams-grid'], // Files can affect exam display in share mode
  });

  // Extract location information from patient and exam data for ShareTopHeader
  const locationInfo = useMemo(() => {
    // Get location from exam data (ExamGridModel has both locationName and locationId)
    const firstExam = examsGridData?.data?.[0];
    if (firstExam?.locationName || firstExam?.locationId) {
      return {
        locationName: firstExam.locationName || undefined,
        locationId: firstExam.locationId || undefined,
        isOpo: firstExam.locationType === 'OPO',
      };
    }

    // Fallback: use patient location_Id if available (but no name available directly)
    if (patient?.location_Id) {
      return {
        locationName: undefined,
        locationId: patient.location_Id,
        isOpo: patient.locationType === 'OPO',
      };
    }

    // No location information available
    return {
      locationName: undefined,
      locationId: undefined,
      isOpo: false,
    };
  }, [patient, examsGridData]);

  // Handle filter toggle
  const handleFilterToggle = useEvent(() => {
    setIsFilterExpanded(!isFilterExpanded);
  });

  return (
    <FileViewerProvider>
      <StyledPage>
        <ShareTopHeader />
        <StyledPatientEditHeader
          patientId={sharePatientId}
          shareContext={{
            senderName,
            senderEmail,
            description,
            locationName: locationInfo.locationName,
            isOpo: locationInfo.isOpo,
          }}
        />

        {/* Only show tab navigation when not in upload-only mode and not in upload mode */}
        {!uploadOnlyMode && !isInUploadMode && (
          <ShareTabNavigation
            selectedTab={selectedTabView}
            onTabChange={handleTabChange}
            onFilterToggle={handleFilterToggle}
            isFilterExpanded={isFilterExpanded}
            selectedFilterName={worklistData.selectedWorklist?.name ?? 'All Files'}
            showFilesTab={showFilesTab}
            showExamsTab={showExamsTab}
          />
        )}

        <StyledMainContent>
          {!integratedViewerFile && (
            <>
              {/* Upload-only mode: show only upload interface */}
              {uploadOnlyMode && (
                <PatientFiles
                  patientId={sharePatientId}
                  gridData={[]} // Empty grid data since we don't want to show existing files
                  onViewFile={NO_OP_VIEW_FILE}
                  onUploadModeChange={handleUploadModeChange}
                  shareContext={{
                    shareLinkId,
                    isShareMode: true,
                  }}
                  isReadOnly={false} // Allow uploads
                  onPatientFilesReady={handlePatientFilesReady}
                  onFileOperationsReady={handleFileOperationsReady}
                  onTagDataReady={handleTagDataReady}
                  onWorklistDataReady={handleWorklistDataReady}
                  onFormSubmissionsReady={handleFormSubmissionsReady}
                  locationId={patient?.location_Id ?? undefined}
                  initialUploadMode={true} // Force upload mode
                  externalSidebarExpanded={false} // No sidebar in upload-only mode
                  onExternalSidebarChange={NO_OP_SIDEBAR_CHANGE}
                  initialTagIds={initialTagIdsFromShare}
                  disableDataFetching={true} // Prevent API calls for existing files
                />
              )}

              {/* Normal mode: show exams or files based on selected tab */}
              {!uploadOnlyMode && selectedTabView === 'Exams' && (
                <StyledExamsSection>
                  <PatientEditExamGrid
                    className=""
                    featureView="Share"
                    donorToolbar={false}
                    columnsState={columnsState}
                    dataState={dataState}
                    gridData={examsGridData ?? EMPTY_DATA_RESULT}
                    handleViewFinalReport={handleViewFinalReport}
                    handleViewAttachments={handleViewAddAttachmentsClick}
                    handleViewDicomImages={handleViewExam}
                    handleChangeStatusSubmit={NO_OP}
                    handleDataStateChange={handleDataStateChange}
                    activeExamToggle={true}
                    handleShareChange={NO_OP_ASYNC}
                    shareToggle={false}
                    setColumnsState={setColumnsState}
                    handleExamUpdate={NO_OP}
                    handleReadClick={NO_OP}
                    handleExpandChange={NO_OP}
                  />
                </StyledExamsSection>
              )}

              {!uploadOnlyMode && selectedTabView === 'Files' && showFilesTab && (
                <PatientFiles
                  patientId={sharePatientId}
                  gridData={examsGridData?.data ?? []}
                  onViewFile={handleViewFile}
                  onUploadModeChange={handleUploadModeChange}
                  shareContext={{
                    shareLinkId,
                    isShareMode: true,
                  }}
                  isReadOnly={!isUploadRoute} // Allow editing when on upload route, read-only otherwise
                  onPatientFilesReady={handlePatientFilesReady}
                  onFileOperationsReady={handleFileOperationsReady}
                  onTagDataReady={handleTagDataReady}
                  onWorklistDataReady={handleWorklistDataReady}
                  onFormSubmissionsReady={handleFormSubmissionsReady}
                  locationId={patient?.location_Id ?? undefined}
                  initialUploadMode={isUploadRoute} // Enable upload mode when on upload route
                  externalSidebarExpanded={isFilterExpanded}
                  onExternalSidebarChange={setIsFilterExpanded}
                  initialTagIds={initialTagIdsFromShare} // Pass initial tag IDs for upload
                />
              )}
            </>
          )}

          {integratedViewerFile && (
            <StyledFileViewerSection>
              <FileViewerIntegrated
                viewingFile={integratedViewerFile}
                viewingFileContext={integratedViewerContext}
                onCloseViewer={handleCloseViewerIntegrated}
                onFileSelection={handleFileSelectionInViewer}
                onFileDeleted={handleFileDeleted}
                onFileRenamed={handleFileRenamed}
                onFileDescriptionUpdated={handleFileDescriptionUpdated}
                onFileTimestampsUpdated={handleFileTimestampsUpdated}
                onFileTagsUpdated={handleFileTagsUpdated}
                onFileDownloaded={handleFileDownloaded}
                allHierarchicalTags={tagData.allHierarchicalTags}
                allTagsFlat={tagData.allFlatTags}
                tagIdToTagMap={tagData.tagIdToTagMap}
                areTagsLoading={tagData.isLoading}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                formSubmissions={(formData?.formSubmissions ?? []) as any}
                areFormSubmissionsLoading={formData?.areFormSubmissionsLoading ?? false}
                onFormSubmissionsUpdated={
                  formData?.handleFormSubmissionsUpdated ??
                  (async () => {
                    /* No-op fallback */
                  })
                }
                patientId={sharePatientId}
                currentPath={persistedWorklistSelection?.currentPath}
                selectedWorklist={persistedWorklistSelection?.selectedWorklist}
                allWorklists={persistedWorklistSelection?.allWorklists}
                stackName={undefined}
              />
            </StyledFileViewerSection>
          )}
        </StyledMainContent>

        {isViewAddAttachmentsModalVisible && selectedExamId != null && (
          <UploadWindow examId={selectedExamId} onChange={handleViewAddAttachmentsChange} onClose={handleViewAddAttachmentsClose} />
        )}
        {isViewFinalReportModalVisible && viewFile != null && <ViewFileWindow title="Final Report" file={viewFile} onClose={handleViewFinalReportClose} />}
      </StyledPage>
    </FileViewerProvider>
  );
});

ShareHome.displayName = 'ShareHome';

const NO_OP = () => {
  // No operation
};
const NO_OP_ASYNC = async () => {
  // No operation
};

// Add a specific no-op for view file operations
const NO_OP_VIEW_FILE = (_file: FileModel, _context: FileModel[]) => {
  // No-op since files viewing is disabled in upload-only mode
};

// Add a specific no-op for sidebar changes
const NO_OP_SIDEBAR_CHANGE = (_expanded: boolean) => {
  // No-op since sidebar is disabled in upload-only mode
};

const StyledPage = styled(Page)`
  grid-template-rows: min-content min-content min-content 1fr;
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
`;

const StyledMainContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: 1fr;
`;

const StyledPatientEditHeader = styled(PatientEditHeader)`
  .accordion-item-header-div {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`;

const StyledExamsSection = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: min-content 1fr;
`;

const StyledFileViewerSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  .file-viewer-page {
    height: 100%;
    overflow: hidden !important;
    width: 100% !important;
    max-height: 100vh !important; //todo: improve auto calculation
    min-height: calc(100vh -200px);
  }
`;
